import React from "react";
import type { Theme } from "./theme";
import type { Box, SceneLayout } from "./frame";
import { clamp01, lerp } from "../core/easing";
import { stagger } from "./motion";

/**
 * Anivaze v4 — design primitives (theme- & frame-aware, deterministic).
 *
 * The "design muscle": a real background, a data-viz stat, kinetic type, and the
 * always-on caption engine. Everything reads sizes from the layout `unit` and
 * colours from the theme palette, so one scene reflows across aspects and
 * re-renders across themes for free. See PLAN §6 & §9.
 */

// ─── Background: gradient / grain / vignette ──────────────────────────────
export const Background: React.FC<{ theme: Theme; frame: { w: number; h: number } }> = ({ theme, frame }) => {
  const s = theme.surface;
  const gid = `v4-surf-${theme.id}`;
  const vid = `v4-vig-${theme.id}`;
  const nid = `v4-grain-${theme.id}`;
  return (
    <>
      <defs>
        {s.kind === "radial" ? (
          <radialGradient id={gid} cx="34%" cy="24%" r="90%">
            <stop offset="0%" stopColor={s.colors[0]} />
            <stop offset="100%" stopColor={s.colors[s.colors.length - 1]} />
          </radialGradient>
        ) : (
          <linearGradient id={gid} gradientTransform={`rotate(${s.angle} 0.5 0.5)`}>
            <stop offset="0%" stopColor={s.colors[0]} />
            <stop offset="100%" stopColor={s.colors[s.colors.length - 1]} />
          </linearGradient>
        )}
        {s.vignette > 0 && (
          <radialGradient id={vid} cx="50%" cy="46%" r="72%">
            <stop offset="55%" stopColor="#000" stopOpacity="0" />
            <stop offset="100%" stopColor="#000" stopOpacity={s.vignette} />
          </radialGradient>
        )}
        {s.grain > 0 && (
          <filter id={nid} x="0" y="0" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves={2} stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
          </filter>
        )}
      </defs>
      <rect x={0} y={0} width={frame.w} height={frame.h} fill={s.kind === "flat" ? s.colors[0] : `url(#${gid})`} />
      {s.grain > 0 && (
        <rect x={0} y={0} width={frame.w} height={frame.h} filter={`url(#${nid})`} opacity={s.grain} style={{ mixBlendMode: "overlay" }} />
      )}
      {s.vignette > 0 && <rect x={0} y={0} width={frame.w} height={frame.h} fill={`url(#${vid})`} />}
    </>
  );
};

// ─── Kicker: small uppercase eyebrow ──────────────────────────────────────
export const Kicker: React.FC<{ text: string; x: number; y: number; theme: Theme; unit: number; opacity?: number }> = ({
  text, x, y, theme, unit, opacity = 1,
}) => (
  <text
    x={x} y={y} textAnchor="middle" dominantBaseline="central"
    fontFamily={theme.caption.family} fontWeight={700}
    fontSize={unit * 2.3} letterSpacing={`${unit * 0.14}px`}
    fill={theme.palette.primary} opacity={opacity}
    style={{ textTransform: "uppercase" }}
  >
    {text}
  </text>
);

// ─── Headline: line-by-line kinetic display type ──────────────────────────
export const Headline: React.FC<{
  lines: string[]; x: number; yTop: number; sizeUnit: number; theme: Theme; unit: number; t: number;
}> = ({ lines, x, yTop, sizeUnit, theme, unit, t }) => {
  const size = unit * sizeUnit;
  const lh = size * 1.04;
  const d = theme.display;
  return (
    <g>
      {lines.map((line, i) => {
        const p = stagger(t, i, { start: 0.1, step: 0.14, dur: 0.5, sig: theme.motion });
        const y = yTop + i * lh + size * 0.5;
        return (
          <text
            key={i}
            x={x} y={y} textAnchor="middle" dominantBaseline="central"
            fontFamily={d.family} fontWeight={d.weight}
            fontSize={size} letterSpacing={`${size * d.tracking}px`}
            fill={theme.palette.ink}
            opacity={p}
            transform={`translate(0 ${lerp(unit * 1.6, 0, p)})`}
            style={d.upper ? { textTransform: "uppercase" } : undefined}
          >
            {line}
          </text>
        );
      })}
    </g>
  );
};

// ─── Donut: animated stat ring + counting number ─────────────────────────
export const Donut: React.FC<{
  cx: number; cy: number; r: number; value: number; sweep: number; theme: Theme; unit: number; role?: "primary" | "positive";
}> = ({ cx, cy, r, value, sweep, theme, unit, role = "positive" }) => {
  const w = unit * 3.4;
  const C = 2 * Math.PI * r;
  const drawn = clamp01(value * sweep) * C;
  const color = role === "primary" ? theme.palette.primary : theme.palette.positive;
  const track = theme.dark ? "rgba(255,255,255,0.12)" : "rgba(15,22,32,0.10)";
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={track} strokeWidth={w} />
      <circle
        cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={w} strokeLinecap="round"
        strokeDasharray={`${drawn} ${C - drawn}`} transform={`rotate(-90 ${cx} ${cy})`}
      />
      <text
        x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
        fontFamily={theme.display.family} fontWeight={theme.display.weight}
        fontSize={unit * 6.4} fill={theme.palette.ink}
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {Math.round(clamp01(value * sweep) * 100)}%
      </text>
    </g>
  );
};

// ─── Caption: always-on, word-highlighted subtitle ────────────────────────
export interface CaptionSeg {
  text: string;
  start: number;
  end: number;
}

export const Caption: React.FC<{ box: Box; segs: CaptionSeg[]; t: number; theme: Theme; unit: number }> = ({
  box, segs, t, theme, unit,
}) => {
  const seg = segs.find((s) => t >= s.start && t < s.end);
  if (!seg) return null;
  const c = theme.caption;
  const size = unit * c.sizeUnit;
  const words = seg.text.split(" ");
  const p = clamp01((t - seg.start) / Math.max(0.001, seg.end - seg.start));
  const active = Math.min(words.length - 1, Math.floor(p * words.length));
  // Pop-in on segment entry.
  const appear = clamp01((t - seg.start) / 0.22);
  const outline = c.box === "outline";
  return (
    <text
      x={box.cx}
      y={box.cy}
      textAnchor="middle"
      dominantBaseline="central"
      xmlSpace="preserve"
      fontFamily={c.family}
      fontWeight={c.weight}
      fontSize={size}
      letterSpacing={`${size * c.tracking}px`}
      opacity={appear}
      stroke={outline ? c.boxColor : undefined}
      strokeWidth={outline ? size * 0.14 : undefined}
      paintOrder="stroke"
      style={{ ...(c.upper ? { textTransform: "uppercase" } : {}), transform: `translateY(${(1 - appear) * unit}px)` }}
    >
      {words.map((w, i) => (
        <tspan key={i} fill={i === active ? c.highlight : c.fill}>
          {(i > 0 ? " " : "") + w}
        </tspan>
      ))}
    </text>
  );
};

// ─── SafeGuides: dev overlay showing the frame/safe/caption zones ─────────
export const SafeGuides: React.FC<{ layout: SceneLayout; theme: Theme }> = ({ layout, theme }) => {
  const stroke = theme.palette.primary;
  const u = layout.unit;
  const dash = `${u} ${u}`;
  const label = (x: number, y: number, text: string) => (
    <text x={x} y={y} fontFamily="monospace" fontSize={u * 1.5} fill={stroke} opacity={0.7} letterSpacing="1">
      {text}
    </text>
  );
  const rect = (b: Box, o: number) => (
    <rect x={b.x} y={b.y} width={b.w} height={b.h} fill="none" stroke={stroke} strokeWidth={1.5} strokeDasharray={dash} opacity={o} />
  );
  return (
    <g pointerEvents="none">
      {rect(layout.safe, 0.5)}
      {rect(layout.content, 0.35)}
      {rect(layout.caption, 0.55)}
      {label(layout.safe.x + u * 0.6, layout.safe.y - u * 0.8, "SAFE")}
      {label(layout.caption.x + u * 0.6, layout.caption.y + u * 2.2, "CAPTION SAFE")}
    </g>
  );
};
